<script setup>
const props = defineProps({
  ann: {
    type: Object,
    required: true,
  },
  editing: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isMenu: {
    type: Boolean,
    default: false,
  },
  isEnd: {
    type: Boolean,
    default: false,
  },
});

import { VideoStore } from "@/front/stores/video_store.js";
import { PositionCalculator } from "@/front/composables/PositionCalculator.js";
import { AnnStore } from "@/front/stores/ann_store.js";
import { PlayerStore } from "@/front/stores/player_store.js";
import { ref, computed, watch, onMounted, onUnmounted } from "vue";

const annStore = AnnStore();
const playerStore = PlayerStore();
const { calculatePosition } = PositionCalculator();

const videoStore = VideoStore();
const baseDesign = videoStore.video.design || {};

const customStyle =
  (props.ann.customStyle && JSON.parse(props.ann.customStyle)) || {};

// Parsed points array for tracking hotspots
const trackingPoints = computed(() => {
  if (!props.ann.points) return null;
  try {
    const points =
      typeof props.ann.points === "string"
        ? JSON.parse(props.ann.points)
        : props.ann.points;

    if (!Array.isArray(points) || points.length < 2) return null;

    // Sort by time in ascending order
    const sortedPoints = points.sort((a, b) => a.time - b.time);
    log.info(5, "📌 DEBUG - Parsed tracking points:", sortedPoints);
    return sortedPoints;
  } catch (e) {
    log.info(1, "Error parsing tracking points:", e);
    return null;
  }
});

// Generate a point map for quick lookups
const pointsMap = computed(() => {
  if (!trackingPoints.value) return null;

  const map = {};
  const points = trackingPoints.value;

  // Add first and last points for easy reference
  map.first = points[0];
  map.last = points[points.length - 1];

  // Map each point by its time
  points.forEach((point) => {
    map[point.time] = point;
  });

  return map;
});

// Determine if this is a tracking hotspot
const isTracking = computed(() => {
  return !!trackingPoints.value;
});

// Calculate current position based on time interpolation
const currentPosition = ref({
  top: props.ann.pos_top + props.ann.pos_height / 2 || customStyle.top || 33,
  left: props.ann.pos_left + props.ann.pos_width / 2 || customStyle.left || 33,
});

// Store animation details for smooth transitions
let currentAnimation = null;
let lastAnimationTime = null;
let animationStartTime = 0;
let animationEndTime = 0;
let startPos = { top: 0, left: 0 };
let endPos = { top: 0, left: 0 };
const ANIMATION_DURATION = 300; // Animation duration in ms - increased for smoother animation

// Find the appropriate start and end points based on current time
function findAnimationPoints(currentTime) {
  const points = trackingPoints.value;
  if (!points) return null;

  log.info(5, "📌 DEBUG - Finding animation points for time:", currentTime);

  // If before first point or after last point
  if (currentTime <= points[0].time) {
    log.info(5, "📌 DEBUG - Before first point, using first point");
    return {
      start: {
        time: points[0].time,
        top: parseFloat(points[0].top),
        left: parseFloat(points[0].left),
      },
      end: {
        time: points[0].time,
        top: parseFloat(points[0].top),
        left: parseFloat(points[0].left),
      },
    };
  }

  if (currentTime >= points[points.length - 1].time) {
    log.info(5, "📌 DEBUG - After last point, using last point");
    const lastPoint = points[points.length - 1];
    return {
      start: {
        time: lastPoint.time,
        top: parseFloat(lastPoint.top),
        left: parseFloat(lastPoint.left),
      },
      end: {
        time: lastPoint.time,
        top: parseFloat(lastPoint.top),
        left: parseFloat(lastPoint.left),
      },
    };
  }

  // Find the two points to animate between
  for (let i = 0; i < points.length - 1; i++) {
    if (currentTime >= points[i].time && currentTime < points[i + 1].time) {
      log.info(
        5,
        `📌 DEBUG - Between points ${i} and ${i + 1}:`,
        points[i],
        points[i + 1]
      );
      return {
        start: {
          time: points[i].time,
          top: parseFloat(points[i].top),
          left: parseFloat(points[i].left),
        },
        end: {
          time: points[i + 1].time,
          top: parseFloat(points[i + 1].top),
          left: parseFloat(points[i + 1].left),
        },
      };
    }
  }

  log.info(5, "📌 DEBUG - No matching points found");
  return null;
}

// Easing function for smooth animation (easeInOutQuad)
function easeInOutQuad(t) {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

// Start a new animation between two points
function startAnimation(fromTime, toTime) {
  log.info(5, "📌 DEBUG - Starting animation from", fromTime, "to", toTime);

  // Cancel any existing animation
  if (currentAnimation) {
    log.info(5, "📌 DEBUG - Canceling existing animation");
    cancelAnimationFrame(currentAnimation);
    currentAnimation = null;
  }

  // Get current decitime
  const currentTime = playerStore.decitime;
  log.info(5, "📌 DEBUG - Current decitime:", currentTime);

  // Find animation points
  const points = findAnimationPoints(currentTime);
  if (!points) {
    log.info(5, "📌 DEBUG - No animation points found, aborting");
    return;
  }

  // Check if we need to skip to a specific position due to seek
  const wasSeeking = Math.abs(fromTime - toTime) > 0.2;
  log.info(
    5,
    "📌 DEBUG - Was seeking:",
    wasSeeking,
    "difference:",
    Math.abs(fromTime - toTime)
  );

  // Get current position (the actual current visual position)
  const currentVisualPos = { ...currentPosition.value };
  log.info(5, "📌 DEBUG - Current visual position:", currentVisualPos);

  // Determine animation start position - use current visual position
  startPos = wasSeeking
    ? {
        top: parseFloat(points.start.top),
        left: parseFloat(points.start.left),
      }
    : currentVisualPos;

  log.info(5, "📌 DEBUG - Animation start position:", startPos);

  // End position is always the end point from the calculation
  endPos = {
    top: parseFloat(points.end.top),
    left: parseFloat(points.end.left),
  };

  log.info(5, "📌 DEBUG - Animation end position:", endPos);

  // Set animation timing
  animationStartTime = performance.now();
  animationEndTime = animationStartTime + ANIMATION_DURATION;

  // If points are the same, just set position and don't animate
  if (startPos.top === endPos.top && startPos.left === endPos.left) {
    log.info(
      5,
      "📌 DEBUG - Start and end positions are the same, skipping animation"
    );
    currentPosition.value = { ...startPos };
    return;
  }

  // Start animation loop
  log.info(5, "📌 DEBUG - Starting animation step loop");
  animateStep();
}

// Animation step function
function animateStep() {
  const now = performance.now();
  // Calculate progress (0 to 1)
  let progress =
    (now - animationStartTime) / (animationEndTime - animationStartTime);

  // Ensure progress is between 0 and 1
  progress = Math.min(1, Math.max(0, progress));

  // Apply easing
  const easedProgress = easeInOutQuad(progress);

  // Calculate new position
  const newLeft = startPos.left + (endPos.left - startPos.left) * easedProgress;
  const newTop = startPos.top + (endPos.top - startPos.top) * easedProgress;

  // Only log if this is a significant change to avoid console flood
  if (progress % 0.1 < 0.01) {
    log.info(
      5,
      `📌 DEBUG - Animation step: progress=${progress.toFixed(
        2
      )}, position=(${newLeft.toFixed(1)}, ${newTop.toFixed(1)})`
    );
  }

  // Update position
  currentPosition.value = {
    top: parseFloat(newTop.toFixed(3)),
    left: parseFloat(newLeft.toFixed(3)),
  };

  // Continue animation if not complete
  if (progress < 1) {
    currentAnimation = requestAnimationFrame(animateStep);
  } else {
    log.info(5, "📌 DEBUG - Animation complete");
    currentAnimation = null;
  }
}

// Watch for video time changes
watch(
  () => playerStore.decitime,
  (newTime, oldTime) => {
    if (!isTracking.value) return;

    log.info(5, "📌 DEBUG - Time changed from", oldTime, "to", newTime);

    // Check if time jumped backwards
    const timeJumpedBackwards = newTime < oldTime;
    if (timeJumpedBackwards) {
      log.info(5, "📌 DEBUG - Time jumped backwards");
    }

    // Don't restart animation for the same time
    if (newTime === lastAnimationTime) {
      log.info(5, "📌 DEBUG - Same time, skipping animation");
      return;
    }

    lastAnimationTime = newTime;

    // Start new animation
    startAnimation(oldTime, newTime);
  },
  { immediate: true }
);

// Apply tracking position or use static position from props
const styling = computed(() => {
  let opacity = props.disabled ? 0.5 : 1;
  let cursor = props.disabled ? "not-allowed" : "pointer";

  // Create a copy of annotation with potentially updated position
  const trackingAnn = { ...props.ann };

  // Override position if tracking
  if (isTracking.value) {
    trackingAnn.pos_top = currentPosition.value.top;
    trackingAnn.pos_left = currentPosition.value.left;
  }

  const dimensions = calculatePosition(trackingAnn);

  // Adjust position to center the hotspot
  const centeredLeft = isTracking.value
    ? dimensions.left - dimensions.width / 2
    : dimensions.left;
  const centeredTop = isTracking.value
    ? dimensions.top - dimensions.height / 2
    : dimensions.top;

  // log.info(5,'📌 DEBUG - Centering adjustment:', {
  //   originalLeft: dimensions.left,
  //   originalTop: dimensions.top,
  //   width: dimensions.width,
  //   height: dimensions.height,
  //   centeredLeft,
  //   centeredTop
  // });

  return {
    width: props.editing ? "100%" : `${dimensions.width}px`,
    height: props.editing ? "100%" : `${dimensions.height}px`,
    left: props.editing ? "0px" : `${centeredLeft}px`,
    top: props.editing ? "0px" : `${centeredTop}px`,
    minHeight: `${dimensions.minHeight}px`,
    fontSize: `${dimensions.fontSize}px`,
    borderRadius: `${props.ann.hotspot_radius}%`,
    "will-change": isTracking.value ? "transform, left, top" : "auto",
  };
});

const spotStyle = computed(() => {
  const ringPercentage =
    props.ann.hotspot_ring_width || customStyle.ringWidth || 33;

  // Create a copy of annotation with potentially updated position
  const trackingAnn = { ...props.ann };

  // // Override position if tracking
  // if (isTracking.value) {
  //   trackingAnn.pos_top = currentPosition.value.top;
  //   trackingAnn.pos_left = currentPosition.value.left;
  // }

  const dimensions = calculatePosition(trackingAnn);
  const ringWidth = ringPercentage * 0.01 * dimensions.width;
  let opacity = props.disabled ? 0.5 : 1;
  let cursor =
    props.disabled || isNoneClickAction.value ? "not-allowed" : "pointer";
  // 0 left, 1 center, 2 right
  let isLeftAligned = props.ann.hotspot_alignment == 0;
  let isCenterAligned =
    !props.ann.hotspot_alignment || props.ann.hotspot_alignment == 1;
  let isRightAligned = props.ann.hotspot_alignment == 2;
  let left = isLeftAligned
    ? `calc(0% + ${ringWidth / 2}px)`
    : isCenterAligned
    ? "50%"
    : `calc(100% - ${ringWidth / 2}px)`;

  let bgColor =
    props.ann.background_color ||
    customStyle.background_color ||
    baseDesign.color;
  if (props.ann.post_click_state == "color" && props.ann.clicked) {
    bgColor =
      props.ann.post_click_color ||
      customStyle.postClickColor ||
      baseDesign.color;
  }
  return {
    circle: {
      backgroundColor: bgColor,
      width: `${ringPercentage}%`,
      height: `${ringWidth}px`,
      position: "absolute",
      left: left,
      top: "50%",
      transform: `translate(-50%, -50%)`, // Centers the circle
      opacity: opacity,
      cursor: cursor,
      backgroundImage: props.ann.background_image
        ? `url('${props.ann.background_image}')`
        : "none",
      backgroundSize: "contain",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
    },
    ring: {
      borderColor: bgColor,
      position: "absolute",
      left: left,
      top: "50%",
      height: `${ringWidth}px`,
      width: `${ringWidth}px`,
      borderWidth: `${ringWidth / 15}px`,
      borderStyle: "solid",
      borderRadius: "50%", // Makes the ring a circle
      opacity: opacity,
      cursor: cursor,
    },
    caption: {
      color: baseDesign.comment_text_color,
      backgroundColor: baseDesign.comment_background_color,
      textAlign: isCenterAligned ? "center" : isLeftAligned ? "left" : "right",
    },
  };
});

// Initialize when component mounts
onMounted(() => {
  // if (isTracking.value) {
  //   const points = findAnimationPoints(playerStore.decitime);
  //   log.info(5,'📌 DEBUG - onMounted Points:', points);
  //   if (points && points[0]) {
  //     currentPosition.value = { left: points[0].left - props.ann.pos_width / 2, top: points[0].top - props.ann.pos_height / 2 };
  //     log.info(5,'📌 DEBUG - Current position:', currentPosition.value);
  //   } else {
  //     currentPosition.value = { left: props.ann.pos_left, top: props.ann.pos_top };
  //     log.info(5,'📌 DEBUG NO POINTS FOUND - Current position:', currentPosition.value);
  //   }
  // }
});

// Clean up when component unmounts
onUnmounted(() => {
  if (currentAnimation) {
    cancelAnimationFrame(currentAnimation);
    currentAnimation = null;
  }
});

const mouseInside = ref(false);
const keyboardFocused = ref(false);

const onMouseEnter = () => {
  if (props.ann.do_spotlight) {
    annStore.startSpotlight(props.ann);
  }
  mouseInside.value = true;
};

const onMouseLeave = () => {
  annStore.endSpotlight();
  mouseInside.value = false;
};

const onFocus = () => {
  keyboardFocused.value = true;
  if (props.ann.do_spotlight) {
    annStore.startSpotlight(props.ann);
  }
  // Announce position to screen reader
  announcePosition();
};

const onBlur = () => {
  keyboardFocused.value = false;
  annStore.endSpotlight();
};

const handleClick = (event) => {
  if (!props.disabled && !isNoneClickAction.value) {
    // Trigger the actual click action
    if (props.ann.click_action) {
      // This would need to be implemented based on your click handling logic
      console.log("Hotspot clicked:", props.ann);
    }
  }
};

const handleKeyActivation = (event) => {
  if (!props.disabled && !isNoneClickAction.value) {
    handleClick(event);
  }
};

const announcePosition = () => {
  // Create announcement for screen reader
  const announcement = getScreenReaderDescription.value;
  const announcer = document.createElement("div");
  announcer.setAttribute("aria-live", "polite");
  announcer.setAttribute("aria-atomic", "true");
  announcer.style.position = "absolute";
  announcer.style.left = "-10000px";
  announcer.textContent = announcement;
  document.body.appendChild(announcer);
  setTimeout(() => document.body.removeChild(announcer), 1000);
};

const showRing = computed(() => {
  return props.ann.hotspot_show_ring;
});

const getHotspotClassList = computed(() => {
  return {
    "bg-black/20": mouseInside.value && props.ann.hotspot_hover,
    "cursor-pointer": !props.disabled,
    spotlightMode: props.ann.do_spotlight,
    spotlightDim:
      annStore.spotlightInteraction &&
      annStore.spotlightInteraction.id != props.ann.id,
  };
});

const isNoneClickAction = computed(() => {
  return props.ann.click_action == "none";
});

// Computed properties for accessibility
const getAriaLabel = computed(() => {
  const caption =
    props.ann.transformed_caption ||
    props.ann.caption ||
    props.ann.extra ||
    "Interactive hotspot";
  const action = props.ann.click_action || "click";
  const position = getPositionDescription();
  return `${caption}. ${position}. Activates on ${action}.`;
});

const getScreenReaderDescription = computed(() => {
  const position = getPositionDescription();
  const caption = props.ann.caption || props.ann.extra || "";
  return `Hotspot ${caption ? `"${caption}" ` : ""}located ${position}`;
});

function getPositionDescription() {
  const currentPos = isTracking.value
    ? currentPosition.value
    : {
        top: props.ann.pos_top,
        left: props.ann.pos_left,
      };

  // Convert position to descriptive text
  let horizontal = "";
  let vertical = "";

  if (currentPos.left < 33) {
    horizontal = "on the left";
  } else if (currentPos.left > 66) {
    horizontal = "on the right";
  } else {
    horizontal = "in the center";
  }

  if (currentPos.top < 33) {
    vertical = "top";
  } else if (currentPos.top > 66) {
    vertical = "bottom";
  } else {
    vertical = "middle";
  }

  return `${vertical} ${horizontal} of the video`;
}
</script>

<template>
  <button
    :style="styling"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    @focus="onFocus"
    @blur="onBlur"
    @click="handleClick"
    @keydown.enter="handleKeyActivation"
    @keydown.space.prevent="handleKeyActivation"
    :class="getHotspotClassList"
    class="cursor-pointer touch-manipulation"
    :data-id="ann.id"
    :disabled="disabled || isNoneClickAction"
    :tabindex="disabled || isNoneClickAction ? '-1' : '0'"
    :aria-label="getAriaLabel"
    :aria-describedby="`hotspot-description-${ann.id}`"
    role="button"
  >
    <div
      class="spot-container relative h-full w-full transform transition-transform pointer-events-none"
      :class="{
        'animate-none border-2 border-red-500':
          annStore.isEditing || playerStore.isDebug,
        [`${ann.animation}`]: true,
        'active:scale-[90%] hover:scale-105 z-10':
          !disabled && !isNoneClickAction,
      }"
    >
      <span
        v-if="showRing && !disabled"
        class="spot-ring"
        :style="spotStyle.ring"
        aria-hidden="true"
      ></span>
      <span
        v-if="showRing"
        class="spot-circle"
        :style="spotStyle.circle"
        aria-hidden="true"
      ></span>
    </div>
    <!-- Show caption if enabled -->
    <div
      v-if="ann.show_caption && (ann.caption || ann.extra)"
      class="text-sm lg:text-base xl:text-2xl w-fit px-3 py-1 rounded-md mx-auto shadow-md pointer-events-none"
      :style="spotStyle.caption"
      :id="`hotspot-description-${ann.id}`"
      v-html="ann.transformed_caption || ann.caption || ann.extra"
    ></div>
    <!-- Screen reader only description for position -->
    <span class="sr-only" v-if="!ann.show_caption">
      {{ getScreenReaderDescription }}
    </span>
  </button>
</template>

<style scoped>
/* Screen reader only class */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

/* Only show focus outline for keyboard navigation, not mouse clicks */
button:focus-visible {
  outline: 2px solid #3b82f6 !important;
  outline-offset: 2px !important;
}

/* Remove default focus outline for mouse users */
button:focus:not(:focus-visible) {
  outline: none;
}

/* Remove default button styles but keep functionality */
button {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: inherit;
  text-align: inherit;
}
</style>
